/**
 * Canonical grain for a Demand Plan layer row.
 *
 * The engine (`forecast_layer_v2`) and the view transform treat `group_level`
 * as a set and pair each field with the value at the same index. Which order
 * the fields were saved in therefore must not matter, but the pairing must be
 * right. Sorting the fields and permuting the values with them gives every
 * writer and reader the same key for the same cell, so a row saved before a
 * column drag and one saved after it merge instead of duplicating (SUNGLAS-56).
 *
 * Plain code-unit comparison, not localeCompare, so the order matches the
 * engine's `list.sort()` on the same field names.
 */
export const canonicalizeDemandPlanGrain = (
  groupLevel: readonly string[],
  groupValue: readonly string[],
): { groupLevel: string[]; groupValue: string[] } => {
  if (groupLevel.length !== groupValue.length) {
    // Malformed row; nothing sensible to permute. Pass through untouched.
    return { groupLevel: [...groupLevel], groupValue: [...groupValue] };
  }
  const order = groupLevel
    .map((_, i) => i)
    .sort((a, b) => {
      if (groupLevel[a] < groupLevel[b]) return -1;
      if (groupLevel[a] > groupLevel[b]) return 1;
      return a - b;
    });
  return {
    groupLevel: order.map((i) => groupLevel[i]),
    groupValue: order.map((i) => groupValue[i]),
  };
};

/**
 * The field order every grid row key and every saved layer row uses: sorted,
 * independent of how the segment chips are displayed. The grid keys its edit
 * maps by this order, so dragging a column neither re-keys pending edits nor
 * changes how a saved row pairs fields with values.
 */
export const canonicalSegmentOrder = (fields: readonly string[]): string[] =>
  [...fields].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
